// Tile layout management for coordinate-based heatmaps

import { isSingleWellLayout, KIT_TYPE_TISSUE } from "./well-layout";

interface TileInfo {
  Name: string;
  XMillimeters: number;
  YMillimeters: number;
}

interface WellInfo {
  WellLocation: string;
  Tiles: TileInfo[];
}

interface TileLayoutJson {
  Wells: WellInfo[];
}

interface TilePosition {
  tileName: string;
  x: number; // Proportional X coordinate (0-1)
  y: number; // Proportional Y coordinate (0-1)
  absoluteX: number;
  absoluteY: number;
  wellLocation: string;
  tileIndex: number; // Global index across all wells
}

export type WellCount = 1 | 12 | 48;

// Import the layout data
const TILE_LAYOUTS: Record<WellCount, () => Promise<{ default: TileLayoutJson }>> = {
  1: () => import('./tile-layouts/TileInfo1Well.json'),
  12: () => import('./tile-layouts/TileInfo12Well.json'),
  48: () => import('./tile-layouts/TileInfo48Well.json'),
};

export class TileLayoutManager {
  private layoutCache = new Map<WellCount, TilePosition[]>();

  async loadLayout(wellCount: WellCount): Promise<TilePosition[]> {
    if (this.layoutCache.has(wellCount)) {
      return this.layoutCache.get(wellCount)!;
    }

    try {
      const layoutModule = await TILE_LAYOUTS[wellCount]();
      const jsonData = layoutModule.default;

      // Collect all tiles with coordinates
      const allTiles: { name: string; x: number; y: number; well: string }[] = [];
      for (const well of jsonData.Wells) {
        for (const tile of well.Tiles) {
          allTiles.push({
            name: tile.Name,
            x: tile.XMillimeters,
            y: tile.YMillimeters,
            well: well.WellLocation,
          });
        }
      }

      // Sort tiles alphabetically by name to match API data order
      allTiles.sort((a, b) => a.name.localeCompare(b.name));

      // Find bounds
      const xs = allTiles.map(t => t.x);
      const ys = allTiles.map(t => t.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      // Create normalized positions - tileIndex now matches alphabetical order (API order)
      const positions: TilePosition[] = allTiles.map((tile, idx) => ({
        tileName: tile.name,
        x: (tile.x - minX) / (maxX - minX),
        y: (tile.y - minY) / (maxY - minY),
        absoluteX: tile.x,
        absoluteY: tile.y,
        wellLocation: tile.well,
        tileIndex: idx,
      }));

      console.log(`Loaded ${positions.length} tiles for ${wellCount} wells`);
      this.layoutCache.set(wellCount, positions);
      return positions;
    } catch (error) {
      console.error(`Failed to load layout for ${wellCount} wells:`, error);
      return [];
    }
  }

  async getCoordinateBounds(wellCount: WellCount): Promise<{
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    width: number;
    height: number;
  }> {
    const positions = await this.loadLayout(wellCount);
    if (positions.length === 0) {
      return { minX: 0, maxX: 1, minY: 0, maxY: 1, width: 1, height: 1 };
    }

    const xs = positions.map(p => p.absoluteX);
    const ys = positions.map(p => p.absoluteY);

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      minX,
      maxX,
      minY,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  // Detect well count from wellLayout string
  detectWellCount(wellLayout: string, kitType?: string): WellCount {
    if (isSingleWellLayout(wellLayout)) return 1;
    if (wellLayout === "FortyEightWell") return 48;
    // A stored layout is always honored. Only when it's missing, infer from the
    // kit: tissue kits run on OneWell or PanosSlide, never 12 or 48 wells.
    if (!wellLayout && kitType === KIT_TYPE_TISSUE) return 1;
    return 12; // Default to 12 wells
  }

  // Create a map from tile name to position
  async getTilePositionMap(wellCount: WellCount): Promise<Map<string, TilePosition>> {
    const positions = await this.loadLayout(wellCount);
    const map = new Map<string, TilePosition>();
    for (const pos of positions) {
      map.set(pos.tileName, pos);
    }
    return map;
  }

  // Get ordered list of tile names (for mapping to data array indices)
  async getTileOrder(wellCount: WellCount): Promise<string[]> {
    const positions = await this.loadLayout(wellCount);
    return positions.map(p => p.tileName);
  }

  // Get per-well tile order as it appears in the original JSON (for mapping to per-well data arrays)
  // Returns: Map<wellLocation, Map<tileName, localIndex>>
  private perWellTileOrderCache = new Map<WellCount, Map<string, Map<string, number>>>();

  async getPerWellTileOrder(wellCount: WellCount): Promise<Map<string, Map<string, number>>> {
    if (this.perWellTileOrderCache.has(wellCount)) {
      return this.perWellTileOrderCache.get(wellCount)!;
    }

    try {
      const layoutModule = await TILE_LAYOUTS[wellCount]();
      const jsonData = layoutModule.default;

      const result = new Map<string, Map<string, number>>();

      for (const well of jsonData.Wells) {
        const tileIndexMap = new Map<string, number>();
        well.Tiles.forEach((tile, idx) => {
          tileIndexMap.set(tile.Name, idx);
        });
        result.set(well.WellLocation, tileIndexMap);
      }

      this.perWellTileOrderCache.set(wellCount, result);
      return result;
    } catch (error) {
      console.error(`Failed to get per-well tile order for ${wellCount} wells:`, error);
      return new Map();
    }
  }
}

// Global instance
export const tileLayoutManager = new TileLayoutManager();

